// src/services/api.ts
import { API_BASE_URL } from '../config';
import { DASHBOARD_VERSION, VERSION_HEADERS } from '../version';
import { 
  ApiError, 
  SubscriptionError, 
  ConversationResponse, 
  ConversationDetails, 
  Conversation,
  Message,
  MerchantSendMessageRequest,
  // Analytics Types
  AnalyticsConfiguration,
  CreateAnalyticsConfigurationRequest,
  ZohoConnectRequest,
  PowerBIConnectRequest,
  OAuthUrlResponse,
  AnalyticsCredentialStatus,
  CredentialRefreshResponse,
  DiscoverSchemaRequest,
  DiscoverSchemaResponse,
  AnalyticsSchemaTable,
  ProviderConfigurationStatus,
  ProviderTestResponse,
  SuccessResponse,
  SchemaCacheStats,
  AnalyticsMessageRequest,
  UpdateConversationRequest,
  UpdateConversationMessageRequest,
  // Document Management Types
  CanonicalDocument,
  UpsertDocumentInput,
  UpsertDocumentsResponse,
  DeleteDocumentResponse,
  BatchGetDocumentsResponse,
  // Dashboard Types
  DashboardOverviewResponse,
  // Admin Dashboard Types
  TenantRecord,
  CreateTenantRequest,
  TenantDeletionCheck,
  TenantDeletionResult,
  SystemStats,
  User,
  GetAllUsersResponse,
  GetAllSubscriptionsResponse,
  AdminSystemStats,
  CrossTenantApiKey,
  GetCrossTenantApiKeysResponse,
  CrossTenantApiKeyFilters,
  // User Creation Types
  CreateMerchantUserRequest,
  CreateSuperAdminUserRequest,
  CreateUserResponse,
  // RAG Configuration Types
  RAGGuidelines,
  RAGGuidelinesDefaults,
  RAGCustomResponseRule,
  RAGTaskCapability,
  RAGTaskConfiguration,
  RAGSettings,
  // Prebuilt Messages Types
  PrebuiltMessage,
  PrebuiltMessageCategory,
  PrebuiltMessageStats,
  // Analytics Guidelines Types
  AnalyticsGuidelines
} from '../types';

const getAuthToken = (): string | null => {
    try {
        return localStorage.getItem('authToken');
    } catch (error) {
        console.error("Could not access localStorage", error);
        return null;
    }
}

const getTenantId = (endpoint?: string): string => {
    try {
        // If it's an admin API call, we should use the user's actual home tenant,
        // not the one they are currently impersonating.
        const isAdminEndpoint = endpoint?.startsWith('/api/admin/');
        
        if (!isAdminEndpoint) {
            const impersonatedTenant = localStorage.getItem('impersonatedTenant');
            if (impersonatedTenant) {
                return impersonatedTenant;
            }
        }

        const storedUser = localStorage.getItem('authUser');
        if (storedUser) {
            const user = JSON.parse(storedUser);
            if (user && user.tenantId) {
                return user.tenantId;
            }
        }
        console.warn('No tenantId found in stored user, falling back to "default"');
        return 'default';
    } catch (error) {
        console.error("Could not access localStorage or parse user data", error);
        console.warn('Falling back to "default" tenant due to error');
        return 'default';
    }
}

export const api = {
  async get<T>(endpoint: string): Promise<T> {
    return request<T>(endpoint, { method: 'GET' });
  },
  async post<T>(endpoint: string, body: any): Promise<T> {
    return request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  async put<T>(endpoint: string, body: any): Promise<T> {
    return request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },
  async delete<T>(endpoint: string): Promise<T> {
    return request<T>(endpoint, { method: 'DELETE' });
  },
  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    return request<T>(endpoint, {
      method: 'POST',
      body: formData,
    }, false); // Don't stringify body for FormData
  },

  // Conversation API methods
  async getConversationHistory(page: number = 1, pageSize: number = 10): Promise<import('../types').ConversationHistoryResponse> {
    return request<import('../types').ConversationHistoryResponse>(
      `/api/merchant/conversations/history?page=${page}&pageSize=${pageSize}`,
      { method: 'GET' }
    );
  },

  async getConversationMessages(
    conversationId: string,
    page: number = 1,
    pageSize: number = 50
  ): Promise<import('../types').PaginatedMessagesResponse> {
    // Note: conversationId parameter is now the MongoDB _id ObjectId
    // The backend expects: /api/merchant/conversations/{_id}/messages
    return request<import('../types').PaginatedMessagesResponse>(
      `/api/merchant/conversations/${conversationId}/messages?page=${page}&pageSize=${pageSize}`,
      { method: 'GET' }
    );
  },

  async sendMessage(messageData: MerchantSendMessageRequest): Promise<ConversationResponse> {
    return request<ConversationResponse>('/api/merchant/conversations', {
      method: 'POST',
      body: JSON.stringify(messageData),
    });
  },

  async getConversation(conversationId: string): Promise<ConversationDetails> {
    return request<ConversationDetails>(`/api/merchant/conversations/${conversationId}`, { method: 'GET' });
  },

  async updateConversation(
    conversationId: string,
    updates: UpdateConversationRequest
  ): Promise<Conversation> {
    return request<Conversation>(`/api/merchant/conversations/${conversationId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  async deleteConversation(conversationId: string): Promise<SuccessResponse | null> {
    return request<SuccessResponse | null>(
      `/api/merchant/conversations/${conversationId}`,
      { method: 'DELETE' }
    );
  },

  async updateConversationMessage(
    conversationId: string,
    messageId: string,
    updates: UpdateConversationMessageRequest
  ): Promise<Message> {
    return request<Message>(
      `/api/merchant/conversations/${conversationId}/messages/${messageId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(updates),
      }
    );
  },

  // Streaming version of updateConversationMessage - returns a Response for SSE handling
  async updateConversationMessageStream(
    conversationId: string,
    messageId: string,
    updates: UpdateConversationMessageRequest
  ): Promise<Response> {
    const token = getAuthToken();
    const headers = new Headers();

    if (token) {
      headers.append('Authorization', `Bearer ${token}`);
    }
    headers.append('X-Tenant-ID', getTenantId(`/api/merchant/conversations/${conversationId}/messages/${messageId}`));
    headers.append('Content-Type', 'application/json');
    headers.append(VERSION_HEADERS.dashboardVersion, DASHBOARD_VERSION.version);

    return fetch(`${API_BASE_URL}/api/merchant/conversations/${conversationId}/messages/${messageId}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(updates),
    });
  },

  // Streaming method - returns a Response object for manual handling
  async sendMessageStream(messageData: MerchantSendMessageRequest): Promise<Response> {
    const token = getAuthToken();
    const headers = new Headers();

    if (token) {
      headers.append('Authorization', `Bearer ${token}`);
    }
    headers.append('X-Tenant-ID', getTenantId('/api/merchant/conversations/stream'));
    headers.append('Content-Type', 'application/json');
    headers.append(VERSION_HEADERS.dashboardVersion, DASHBOARD_VERSION.version);

    return fetch(`${API_BASE_URL}/api/merchant/conversations/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify(messageData),
    });
  },

  // Analytics Configuration API
  async getAnalyticsConfiguration(): Promise<AnalyticsConfiguration> {
    return request<AnalyticsConfiguration>('/api/analytics/configuration', { method: 'GET' });
  },

  async createAnalyticsConfiguration(config: CreateAnalyticsConfigurationRequest): Promise<AnalyticsConfiguration> {
    return request<AnalyticsConfiguration>('/api/analytics/configuration', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  },

  async updateAnalyticsConfiguration(config: Partial<CreateAnalyticsConfigurationRequest>): Promise<AnalyticsConfiguration> {
    try {
      return await request<AnalyticsConfiguration>('/api/analytics/configuration', {
        method: 'PUT',
        body: JSON.stringify(config),
      });
    } catch (error: any) {
      // If configuration does not exist yet (fresh reset), fallback to create via POST
      const status = (error && (error.status || error?.response?.status)) ?? undefined;
      const msg = typeof error?.message === 'string' ? error.message : '';
      const looksLikeNotFound =
        status === 404 ||
        /(not\s*found|configuration not found|no\s+analytics\s+configuration\s+exists)/i.test(msg || '');
      if (looksLikeNotFound) {
        return await request<AnalyticsConfiguration>('/api/analytics/configuration', {
          method: 'POST',
          body: JSON.stringify(config),
        });
      }
      throw error;
    }
  },

  async deleteAnalyticsConfiguration(): Promise<SuccessResponse> {
    return request<SuccessResponse>('/api/analytics/configuration', { method: 'DELETE' });
  },

  // Provider Configuration API
  async getProviderConfiguration(provider: 'zoho' | 'powerbi'): Promise<ProviderConfigurationStatus> {
    return request<ProviderConfigurationStatus>(`/api/analytics/configuration/providers/${provider}`, { method: 'GET' });
  },

  async testProviderConfiguration(provider: 'zoho' | 'powerbi'): Promise<ProviderTestResponse> {
    return request<ProviderTestResponse>(`/api/analytics/configuration/providers/${provider}/test`, { method: 'POST' });
  },

  // OAuth Flow API
  async connectZohoAnalytics(connectRequest: ZohoConnectRequest): Promise<OAuthUrlResponse> {
    return request<OAuthUrlResponse>('/api/analytics/zoho/connect', {
      method: 'POST',
      body: JSON.stringify(connectRequest),
    });
  },

  async connectPowerBIAnalytics(connectRequest: PowerBIConnectRequest): Promise<OAuthUrlResponse> {
    return request<OAuthUrlResponse>('/api/analytics/powerbi/connect', {
      method: 'POST',
      body: JSON.stringify(connectRequest),
    });
  },

  // Credential Management API
  async getAnalyticsCredentialStatus(provider: 'zoho' | 'powerbi'): Promise<AnalyticsCredentialStatus> {
    return request<AnalyticsCredentialStatus>(`/api/analytics/credentials/${provider}`, { method: 'GET' });
  },

  async refreshAnalyticsCredentials(provider: 'zoho' | 'powerbi'): Promise<CredentialRefreshResponse> {
    return request<CredentialRefreshResponse>(`/api/analytics/credentials/${provider}/refresh`, { method: 'POST' });
  },

  async revokeAnalyticsCredentials(provider: 'zoho' | 'powerbi'): Promise<SuccessResponse> {
    // Backend expects DELETE on /refresh to revoke credentials
    return request<SuccessResponse>(`/api/analytics/credentials/${provider}/refresh`, { method: 'DELETE' });
  },

  // Disable a single provider without deleting the entire analytics configuration
  async disableProviderConfiguration(
    provider: 'zoho' | 'powerbi',
    defaultProvider?: 'zoho' | 'powerbi'
  ): Promise<AnalyticsConfiguration> {
    const providerBody: any = {
      enabled: false,
      clientId: null,
      clientSecret: null,
      redirectUri: null,
      defaultScope: null,
      ...(provider === 'zoho' ? { defaultRegion: null } : {})
    };

    const body: Partial<CreateAnalyticsConfigurationRequest> = {
      [provider]: providerBody,
      ...(defaultProvider ? { defaultProvider } : {})
    } as any;

    return request<AnalyticsConfiguration>('/api/analytics/configuration', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  // Schema Discovery API
  async discoverSchema(schemaRequest: DiscoverSchemaRequest): Promise<DiscoverSchemaResponse> {
    return request<DiscoverSchemaResponse>('/api/analytics/schema/discover', {
      method: 'POST',
      body: JSON.stringify(schemaRequest),
    });
  },

  async getCachedSchema(provider: 'zoho' | 'powerbi', options?: {
    forceRefresh?: boolean;
    maxTables?: number;
    includeMetadata?: boolean;
    maxAge?: number;
  }): Promise<DiscoverSchemaResponse> {
    let url = `/api/analytics/schema/cache/${provider}`;
    if (options) {
      const params = new URLSearchParams();
      Object.entries(options).forEach(([key, value]) => {
        if (value !== undefined) params.append(key, value.toString());
      });
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
    }
    return request<DiscoverSchemaResponse>(url, { method: 'GET' });
  },

  async clearProviderCache(provider: 'zoho' | 'powerbi'): Promise<SuccessResponse> {
    return request<SuccessResponse>(`/api/analytics/schema/cache/${provider}`, { method: 'DELETE' });
  },

  async getTableSchema(provider: 'zoho' | 'powerbi', tableName: string, forceRefresh?: boolean): Promise<{ table: AnalyticsSchemaTable }> {
    let url = `/api/analytics/schema/cache/${provider}/tables/${encodeURIComponent(tableName)}`;
    if (forceRefresh) {
      url += '?forceRefresh=true';
    }
    return request<{ table: AnalyticsSchemaTable }>(url, { method: 'GET' });
  },

  async getSchemaCacheStats(): Promise<SchemaCacheStats> {
    return request<SchemaCacheStats>('/api/analytics/schema/cache/stats', { method: 'GET' });
  },

  async clearAllSchemaCache(): Promise<SuccessResponse> {
    return request<SuccessResponse>('/api/analytics/schema/cache/clear', { method: 'DELETE' });
  },

  async refreshSchemaCache(options?: {
    providers?: ('zoho' | 'powerbi')[];
    maxTables?: number;
    includeMetadata?: boolean;
  }): Promise<{ zoho?: DiscoverSchemaResponse; powerbi?: DiscoverSchemaResponse }> {
    return request('/api/analytics/schema/cache/refresh', {
      method: 'POST',
      body: JSON.stringify(options || {}),
    });
  },

  // Analytics Conversations API
  async sendAnalyticsMessage(messageData: AnalyticsMessageRequest): Promise<ConversationResponse> {
    return request<ConversationResponse>('/api/merchant/conversations', {
      method: 'POST',
      body: JSON.stringify(messageData),
    });
  },

  async sendAnalyticsMessageStream(messageData: AnalyticsMessageRequest): Promise<Response> {
    const token = getAuthToken();
    const headers = new Headers();

    if (token) {
      headers.append('Authorization', `Bearer ${token}`);
    }
    headers.append('X-Tenant-ID', getTenantId('/api/merchant/conversations/stream'));
    headers.append('Content-Type', 'application/json');
    headers.append(VERSION_HEADERS.dashboardVersion, DASHBOARD_VERSION.version);

    return fetch(`${API_BASE_URL}/api/merchant/conversations/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify(messageData),
    });
  },

  // RAG Replace & Multipart convenience methods
  async startRAGContextReplace(): Promise<import('../types').ReplaceBatchStartResponse> {
    // Send an explicit empty JSON body so proxies/servers don't hang on a POST with Content-Type but no body
    return request('/api/rag/context/replace/start', { method: 'POST', body: JSON.stringify({}) });
  },

  async finalizeRAGContextReplace(body: { batchId: string; keys: string[]; expiresInSeconds?: number }): Promise<import('../types').FinalizeReplaceResponse> {
    return request('/api/rag/context/replace/finalize', { method: 'POST', body: JSON.stringify(body) });
  },

  async initiateMultipartUpload(body: { batchId: string; fileName: string; contentType: string; fileSize: number; kmsKeyId?: string }): Promise<import('../types').InitiateMultipartUploadResponse> {
    return request('/api/uploads/multipart/initiate', { method: 'POST', body: JSON.stringify(body) })
  },

  async signMultipartUploadPart(body: { key: string; uploadId: string; partNumber: number; expiresInSeconds?: number }): Promise<import('../types').SignMultipartUploadPartResponse> {
    return request('/api/uploads/multipart/sign-part', { method: 'POST', body: JSON.stringify(body) })
  },

  async completeMultipartUpload(body: { batchId: string; key: string; uploadId: string; parts: { ETag: string; PartNumber: number }[]; originalName: string; contentType: string; size: number }): Promise<{ success: boolean }> {
    return request('/api/uploads/multipart/complete', { method: 'POST', body: JSON.stringify(body) })
  },

  async getRAGContextBatch(batchId: string): Promise<import('../types').BatchStatusResponse> {
    return request(`/api/rag/context/batches/${encodeURIComponent(batchId)}`, { method: 'GET' })
  },

  // Documents Import API (triggers Lambda parse from S3)
  async importDocumentsFromS3(body: { keys: string[]; bucket?: string; format?: 'auto' | 'csv' | 'json' | 'xml'; options?: any }): Promise<{ enqueued?: boolean; keys?: number }> {
    return request('/api/documents/import/s3', { method: 'POST', body: JSON.stringify(body) })
  },

  // Document CRUD APIs
  /**
   * Get a single document by ID
   */
  async getDocument(id: string): Promise<CanonicalDocument> {
    return request<CanonicalDocument>(`/api/documents/${encodeURIComponent(id)}`, { method: 'GET' });
  },

  /**
   * Upsert (create or update) documents synchronously
   * Backend will handle version management and indexing
   */
  async upsertDocuments(documents: UpsertDocumentInput[]): Promise<UpsertDocumentsResponse> {
    return request<UpsertDocumentsResponse>('/api/documents', {
      method: 'POST',
      body: JSON.stringify({ documents })
    });
  },

  /**
   * Delete a document by ID (soft delete and deindex from RAG)
   */
  async deleteDocument(id: string): Promise<DeleteDocumentResponse> {
    return request<DeleteDocumentResponse>(`/api/documents/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  /**
   * Batch get documents by IDs
   */
  async batchGetDocuments(ids: string[]): Promise<BatchGetDocumentsResponse> {
    return request<BatchGetDocumentsResponse>('/api/documents/batch-get', {
      method: 'POST',
      body: JSON.stringify({ ids })
    });
  },

  // Dashboard API
  async getDashboardOverview(startDate?: string, endDate?: string): Promise<import('../types').DashboardOverviewResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const queryString = params.toString();
    const url = `/api/dashboard/overview${queryString ? `?${queryString}` : ''}`;

    return request<import('../types').DashboardOverviewResponse>(url, { method: 'GET' });
  },

  // ==================== RAG Configuration API ====================
  
  // RAG Guidelines
  async getRAGGuidelines(): Promise<RAGGuidelines | null> {
    return request<RAGGuidelines | null>('/api/rag/guidelines', { method: 'GET' });
  },

  async upsertRAGGuidelines(guidelines: Partial<RAGGuidelines>): Promise<RAGGuidelines> {
    return request<RAGGuidelines>('/api/rag/guidelines', {
      method: 'PUT',
      body: JSON.stringify(guidelines),
    });
  },

  async getRAGDefaultGuidelines(): Promise<RAGGuidelinesDefaults> {
    return request<RAGGuidelinesDefaults>('/api/rag/guidelines/defaults', { method: 'GET' });
  },

  // RAG Custom Rules
  async listRAGCustomRules(): Promise<RAGCustomResponseRule[]> {
    return request<RAGCustomResponseRule[]>('/api/rag/custom-rules', { method: 'GET' });
  },

  async upsertRAGCustomRule(rule: RAGCustomResponseRule): Promise<RAGCustomResponseRule> {
    return request<RAGCustomResponseRule>('/api/rag/custom-rules', {
      method: 'PUT',
      body: JSON.stringify(rule),
    });
  },

  async deleteRAGCustomRule(ruleId: string): Promise<{ success: boolean }> {
    return request<{ success: boolean }>(`/api/rag/custom-rules/${encodeURIComponent(ruleId)}`, { method: 'DELETE' });
  },

  // RAG Task Capabilities
  async listRAGTaskCapabilities(): Promise<RAGTaskCapability[]> {
    return request<RAGTaskCapability[]>('/api/rag/tasks/capabilities', { method: 'GET' });
  },

  async upsertRAGTaskCapability(capability: RAGTaskCapability): Promise<RAGTaskCapability> {
    return request<RAGTaskCapability>('/api/rag/tasks/capabilities', {
      method: 'PUT',
      body: JSON.stringify(capability),
    });
  },

  async deleteRAGTaskCapability(id: string): Promise<{ success: boolean }> {
    return request<{ success: boolean }>(`/api/rag/tasks/capabilities/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  // RAG Task Configuration
  async getRAGTaskConfiguration(): Promise<RAGTaskConfiguration | null> {
    return request<RAGTaskConfiguration | null>('/api/rag/tasks/configuration', { method: 'GET' });
  },

  async upsertRAGTaskConfiguration(config: Partial<RAGTaskConfiguration>): Promise<RAGTaskConfiguration> {
    return request<RAGTaskConfiguration>('/api/rag/tasks/configuration', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  },

  // RAG Settings
  async getRAGSettings(): Promise<RAGSettings | null> {
    return request<RAGSettings | null>('/api/rag/settings', { method: 'GET' });
  },

  async updateRAGSettings(settings: Partial<RAGSettings>): Promise<RAGSettings> {
    return request<RAGSettings>('/api/rag/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  },

  // ==================== Prebuilt Messages API ====================

  async listPrebuiltMessages(filters?: {
    category?: PrebuiltMessageCategory;
    messageType?: string;
    isActive?: boolean;
    locale?: string;
  }): Promise<PrebuiltMessage[]> {
    const queryParams = new URLSearchParams();
    if (filters?.category) queryParams.set('category', filters.category);
    if (filters?.messageType) queryParams.set('messageType', filters.messageType);
    if (filters?.isActive !== undefined) queryParams.set('isActive', String(filters.isActive));
    if (filters?.locale) queryParams.set('locale', filters.locale);
    
    const queryString = queryParams.toString();
    const url = `/api/prebuilt-messages${queryString ? `?${queryString}` : ''}`;
    return request<PrebuiltMessage[]>(url, { method: 'GET' });
  },

  async createPrebuiltMessage(message: Omit<PrebuiltMessage, '_id' | 'tenant' | 'usageCount' | 'createdAt' | 'updatedAt'>): Promise<PrebuiltMessage> {
    return request<PrebuiltMessage>('/api/prebuilt-messages', {
      method: 'POST',
      body: JSON.stringify(message),
    });
  },

  async updatePrebuiltMessage(messageId: string, updates: Partial<PrebuiltMessage>): Promise<PrebuiltMessage> {
    return request<PrebuiltMessage>(`/api/prebuilt-messages/${encodeURIComponent(messageId)}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  async deletePrebuiltMessage(messageId: string): Promise<{ success: boolean }> {
    return request<{ success: boolean }>(`/api/prebuilt-messages/${encodeURIComponent(messageId)}`, { method: 'DELETE' });
  },

  async getPrebuiltMessageStats(category?: PrebuiltMessageCategory): Promise<PrebuiltMessageStats> {
    const url = category 
      ? `/api/prebuilt-messages/stats?category=${encodeURIComponent(category)}`
      : '/api/prebuilt-messages/stats';
    return request<PrebuiltMessageStats>(url, { method: 'GET' });
  },

  // ==================== Analytics Guidelines API ====================

  async getAnalyticsGuidelines(): Promise<AnalyticsGuidelines | null> {
    return request<AnalyticsGuidelines | null>('/api/analytics/guidelines', { method: 'GET' });
  },

  async upsertAnalyticsGuidelines(guidelines: Partial<AnalyticsGuidelines>): Promise<AnalyticsGuidelines> {
    return request<AnalyticsGuidelines>('/api/analytics/guidelines', {
      method: 'PUT',
      body: JSON.stringify(guidelines),
    });
  },

  async deleteAnalyticsGuidelines(): Promise<{ deletedCount: number }> {
    return request<{ deletedCount: number }>('/api/analytics/guidelines', { method: 'DELETE' });
  }
};

async function request<T>(endpoint: string, options: RequestInit, stringifyBody = true): Promise<T> {
  const token = getAuthToken();
  const headers = new Headers();
  
  if (stringifyBody) {
      headers.append('Content-Type', 'application/json');
  }

  if (token) {
    headers.append('Authorization', `Bearer ${token}`);
  }
  // Dynamic tenant ID from authenticated user (respects impersonation for non-admin calls)
  headers.append('X-Tenant-ID', getTenantId(endpoint));
  // Dashboard version for tracking and compatibility
  headers.append(VERSION_HEADERS.dashboardVersion, DASHBOARD_VERSION.version);


  const config: RequestInit = {
    ...options,
    headers,
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: response.statusText }));

    // If the backend indicates the session is invalid/expired, proactively clear auth
    // state and navigate the app to the login route. We use the hash router used by
    // the app, so setting window.location.hash will trigger navigation to "#/login".
    try {
      if (errorData && errorData.code === 'AUTH0003') {
        try {
          localStorage.removeItem('authToken');
          localStorage.removeItem('authUser');
        } catch (e) {
          // ignore storage errors
        }

        // Dispatch a global event so higher-level app code can handle logout + redirect
        try {
          if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
            window.dispatchEvent(new CustomEvent('auth:expired'));
          } else if (typeof window !== 'undefined' && window.location) {
            // fallback for environments without event support
            window.location.hash = '#/login';
          }
        } catch (e) {
          // non-fatal: keep going to throw the original API error below
          console.error('Failed to dispatch auth:expired event', e);
        }
      }
    } catch (e) {
      // non-fatal: don't let an internal error mask the original API error
      console.error('Error handling AUTH0003 response', e);
    }

    const apiError: ApiError = {
      code: errorData.code,
      userTitle: errorData.userTitle,
      userMessage: errorData.userMessage,
      technicalDescription: errorData.technicalDescription,
      message: errorData.message || 'An API error occurred'
    };

    throw apiError;
  }
  
  if (response.status === 204) { // No Content
      return null as T;
  }

  return response.json();
}

// ==================== Admin Dashboard API Methods ====================

export const adminApi = {
  // Tenant Management
  async getAllTenants(): Promise<TenantRecord[]> {
    const response = await request<{ tenants: TenantRecord[] }>('/api/admin/tenants', { method: 'GET' });
    return response.tenants;
  },

  async getTenant(tenantId: string): Promise<TenantRecord> {
    return request<TenantRecord>(`/api/admin/tenants/${tenantId}`, { method: 'GET' });
  },

  async createTenant(data: CreateTenantRequest): Promise<TenantRecord> {
    return request<TenantRecord>('/api/admin/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async deleteTenant(tenantId: string): Promise<TenantDeletionResult> {
    return request<TenantDeletionResult>(`/api/admin/tenants/${tenantId}`, { method: 'DELETE' });
  },

  async checkTenantDeletion(tenantId: string): Promise<TenantDeletionCheck> {
    return request<TenantDeletionCheck>(`/api/admin/tenants/${tenantId}/deletion-check`, { method: 'GET' });
  },

  // User Management
  async getAllUsers(params?: {
    page?: number;
    limit?: number;
    userType?: string;
    status?: string;
  }): Promise<GetAllUsersResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', params.page.toString());
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.userType) queryParams.set('userType', params.userType);
    if (params?.status) queryParams.set('status', params.status);
    
    const queryString = queryParams.toString();
    const url = `/api/admin/users${queryString ? `?${queryString}` : ''}`;
    return request<GetAllUsersResponse>(url, { method: 'GET' });
  },

  async getUsersByTenant(tenantId: string): Promise<User[]> {
    return request<User[]>(`/api/admin/tenants/${tenantId}/users`, { method: 'GET' });
  },

  async updateUserStatus(userId: string, status: string): Promise<{ success: boolean }> {
    return request<{ success: boolean }>(`/api/admin/users/${userId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  },

  async updateUser(userId: string, data: { personalInfo?: { firstName?: string; lastName?: string; email?: string; language?: string }; status?: string; tenantId?: string }): Promise<{ success: boolean }> {
    return request<{ success: boolean }>(`/api/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteUser(userId: string): Promise<{ success: boolean }> {
    return request<{ success: boolean }>(`/api/admin/users/${userId}`, { method: 'DELETE' });
  },

  // User Creation (Super Admin only)
  async createMerchantUser(tenantId: string, data: CreateMerchantUserRequest): Promise<CreateUserResponse> {
    // Use a custom header to specify the target tenant for user creation
    const token = localStorage.getItem('authToken');
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    if (token) {
      headers.append('Authorization', `Bearer ${token}`);
    }
    headers.append('X-Tenant-ID', tenantId);
    headers.append(VERSION_HEADERS.dashboardVersion, DASHBOARD_VERSION.version);

    const response = await fetch(`${API_BASE_URL}/api/users/merchant`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw {
        code: errorData.code,
        userTitle: errorData.userTitle,
        userMessage: errorData.userMessage,
        message: errorData.message || 'Failed to create merchant user'
      };
    }

    return response.json();
  },

  async createSuperAdminUser(data: CreateSuperAdminUserRequest): Promise<CreateUserResponse> {
    return request<CreateUserResponse>('/api/admin/users/super-admin', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Subscription Management
  async getAllSubscriptions(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<GetAllSubscriptionsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', params.page.toString());
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.status) queryParams.set('status', params.status);
    
    const queryString = queryParams.toString();
    const url = `/api/admin/subscriptions${queryString ? `?${queryString}` : ''}`;
    return request<GetAllSubscriptionsResponse>(url, { method: 'GET' });
  },

  async updateSubscription(subscriptionId: string, data: {
    types?: string[];
    status?: string;
    planId?: string;
    billing?: {
      amount?: number;
      nextBillingDate?: string;
    };
    applyNextPeriod?: boolean;
  }): Promise<{ success: boolean }> {
    return request<{ success: boolean }>(`/api/admin/subscriptions/${subscriptionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Impersonation
  async impersonateTenant(tenantId: string): Promise<{ token: string }> {
    return request<{ token: string }>('/api/admin/impersonate', {
      method: 'POST',
      body: JSON.stringify({ tenantId }),
    });
  },

  async stopImpersonation(): Promise<{ token: string }> {
    return request<{ token: string }>('/api/admin/impersonate/stop', { method: 'POST' });
  },

  // Cross-Tenant System Stats (High Priority Admin Features)
  async getSystemStats(forceRefresh: boolean = false): Promise<AdminSystemStats> {
    const url = `/api/admin/system-stats${forceRefresh ? '?forceRefresh=true' : ''}`;
    const response = await request<{ stats: AdminSystemStats }>(url, { method: 'GET' });
    if (!response || !response.stats) {
      throw new Error('Invalid response: missing stats object');
    }
    return response.stats;
  },

  // Cross-Tenant API Keys
  async getAllApiKeys(filters?: CrossTenantApiKeyFilters): Promise<GetCrossTenantApiKeysResponse> {
    const queryParams = new URLSearchParams();
    if (filters?.tenantId) queryParams.set('tenantId', filters.tenantId);
    if (filters?.status) queryParams.set('status', filters.status);
    if (filters?.type) queryParams.set('type', filters.type);
    if (filters?.page) queryParams.set('page', filters.page.toString());
    if (filters?.limit) queryParams.set('limit', filters.limit.toString());
    if (filters?.sortBy) queryParams.set('sortBy', filters.sortBy);
    if (filters?.sortOrder) queryParams.set('sortOrder', filters.sortOrder);
    if (filters?.search) queryParams.set('search', filters.search);
    
    const queryString = queryParams.toString();
    const url = `/api/admin/api-keys${queryString ? `?${queryString}` : ''}`;
    const response = await request<GetCrossTenantApiKeysResponse>(url, { method: 'GET' });
    return response;
  },
};
